/**
 * TenantHeader Component
 *
 * Displays the current tenant context in a compact header bar for authenticated pages.
 * Shows:
 * - Current company name (or tenantId as fallback)
 * - Current role(s) as status badges
 * - "Switch Company" link for multi-tenant users (navigates back to Company Selector)
 *
 * Requirements: 9.1, 9.3, 9.5
 */

import {
  Badge,
  Box,
  Button,
  SpaceBetween,
} from '@cloudscape-design/components';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

/**
 * Renders a header bar showing the active tenant context.
 * Only renders when a tenant is selected. For users with multiple
 * tenant memberships, provides a link to switch companies.
 */
export const TenantHeader: React.FC = () => {
  const { activeTenant, user, pendingTenants } = useAuth();
  const navigate = useNavigate();

  // Don't render if no tenant context is established
  if (!activeTenant) {
    return null;
  }

  const companyDisplayName = activeTenant.companyName || activeTenant.tenantId;
  const roles = activeTenant.roles ?? [];

  // User can switch if they had multiple tenants at login
  // (pendingTenants is cleared after selection, so check stored membership count)
  const canSwitchCompany = pendingTenants.length > 1 || sessionStorage.getItem('multiTenantUser') === 'true';

  const handleSwitchCompany = () => {
    // Mark for return to selector
    sessionStorage.setItem('multiTenantUser', 'true');
    navigate('/select-company');
  };

  return (
    <div
      role="banner"
      aria-label="Tenant context"
      style={{
        padding: '8px 20px',
        backgroundColor: '#f2f3f3',
        borderBottom: '1px solid #d5dbdb',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <SpaceBetween direction="horizontal" size="m">
        <Box variant="span" fontWeight="bold" color="text-body-secondary">
          {companyDisplayName}
        </Box>
        {roles.length > 0 && (
          <SpaceBetween direction="horizontal" size="xxs">
            {roles.map((role) => (
              <Badge key={role} color="blue">
                {role}
              </Badge>
            ))}
          </SpaceBetween>
        )}
      </SpaceBetween>

      <SpaceBetween direction="horizontal" size="s">
        {user && (
          <Box variant="span" color="text-body-secondary" fontSize="body-s">
            {user.email}
          </Box>
        )}
        {canSwitchCompany && (
          <Button
            variant="link"
            onClick={handleSwitchCompany}
            ariaLabel="Switch to a different company"
          >
            Switch Company
          </Button>
        )}
      </SpaceBetween>
    </div>
  );
};
